import React from 'react';
import { Lock } from 'lucide-react';
import { checkPermission } from '../../utils/auth';
import { PERMISSION_LABELS } from '../../utils/permissions';

export function getPermissionLabel(permission) {
  if (!permission) return null;
  return PERMISSION_LABELS[permission] || permission;
}

export function PermissionPlaceholder({
  permission,
  variant = 'inline',
  title = "You don't have access",
  description = 'Ask an organization admin to update your role or permissions.',
  className = '',
}) {
  const permissionLabel = getPermissionLabel(permission);

  if (variant === 'page') {
    return (
      <div
        className={`flex min-h-[60vh] flex-col items-center justify-center px-6 text-center ${className}`}
        data-permission-placeholder="page"
        data-required-permission={permission || undefined}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-inset">
          <Lock className="h-5 w-5 text-ink-muted" aria-hidden="true" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-ink">{title}</h2>
        <p className="mt-1 max-w-sm text-sm text-ink-secondary">{description}</p>
        {permissionLabel ? (
          <p className="mt-3 rounded-full border border-edge bg-white px-3 py-1 text-xs font-medium text-ink-muted">
            Required: {permissionLabel}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 rounded-md border border-edge bg-paper-inset px-3 py-2.5 ${className}`}
      data-permission-placeholder="inline"
      data-required-permission={permission || undefined}
    >
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="text-[12px] text-ink-muted">{description}</p>
        {permissionLabel ? (
          <p className="mt-1 text-[12px] font-medium text-ink-muted">Required: {permissionLabel}</p>
        ) : null}
      </div>
    </div>
  );
}

export function PermissionBoundary({
  permission,
  children,
  fallback,
  variant = 'inline',
  title,
  description,
  className,
}) {
  if (!permission || checkPermission(permission)) {
    return children;
  }

  return (
    fallback || (
      <PermissionPlaceholder
        permission={permission}
        variant={variant}
        title={title}
        description={description}
        className={className}
      />
    )
  );
}

export default PermissionPlaceholder;
